<template>
    <app-layout>
        <Head title="Processing"/>
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                {{ headerTitle }}
            </h2>
        </template>

        <div class="max-w-4xl mx-auto py-10 sm:px-6 lg:px-8">
            <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg">
                <div class="p-8 text-center">
                    
                    <!-- Loading State -->
                    <div v-if="status === 'checking'" class="space-y-6">
                        <div class="text-6xl text-blue-500">
                            <font-awesome-icon
                                :icon="['far', 'spinner']"
                                class="fa-spin"
                            />
                        </div>
                        <div>
                            <h3 class="text-lg font-medium text-gray-900 mb-2">
                                {{ loadingMessage }}
                            </h3>
                            <p class="text-sm text-gray-600">
                                This usually takes just a few seconds...
                            </p>
                        </div>
                    </div>

                    <!-- Success State -->
                    <div v-else-if="status === 'success'" class="space-y-6">
                        <div class="text-6xl text-green-500">
                            <font-awesome-icon
                                :icon="['far', 'check-circle']"
                                class="animate-pulse"
                            />
                        </div>
                        <div>
                            <h3 class="text-lg font-medium text-gray-900 mb-2">
                                {{ successMessage }}
                            </h3>
                            <p class="text-sm text-gray-600">
                                Redirecting you now...
                            </p>
                        </div>
                    </div>

                    <!-- Error State -->
                    <div v-else-if="status === 'error'" class="space-y-6">
                        <div class="text-6xl text-red-500">
                            <font-awesome-icon
                                :icon="['far', 'times-circle']"
                            />
                        </div>
                        <div>
                            <h3 class="text-lg font-medium text-gray-900 mb-2">
                                {{ errorMessage }}
                            </h3>
                            <p class="text-sm text-gray-600 mb-4">
                                {{ errorDetails }}
                            </p>


                            <div class="flex items-center justify-center space-x-4 mb-4 pt-4">
                                <button 
                                    @click="retry" 
                                    class="inline-flex items-center px-4 py-2 bg-gray-800 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 active:bg-gray-900 focus:outline-none focus:border-gray-900 focus:ring focus:ring-gray-300 disabled:opacity-25 transition"
                                >
                                    Try Again
                                </button>
                                <a href='#' class="mr-2 px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900" 
                                        v-on:click="openIntercom" 
                                >
                                    Contact Support
                                </a>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </app-layout>
</template>

<script>
import AppLayout from "../../Layouts/AppLayout";

export default {
    name: 'StripeLoading',
    components: {
        AppLayout,
    },
    props: {
        context: {
            type: String,
            default: 'payment'
        },
        sessionId: {
            type: String,
            required: false
        }
    },
    data() {
        return {
            status: 'checking', // checking, success, error
            tries: 0,
            maxTries: 40, // 20 seconds at 500ms intervals
            pollInterval: null,
        }
    },
    computed: {
        headerTitle() {
            const titles = {
                'subscription': 'Setting Up Your Subscription',
                'upgrade': 'Upgrading Your Plan', 
                'payment': 'Processing Payment',
            };
            return titles[this.context] || 'Processing';
        },
        loadingMessage() {
            const messages = {
                'subscription': 'Setting up your new subscription...',
                'upgrade': 'Upgrading your plan...',
                'payment': 'Processing your payment...',
            };
            return messages[this.context] || 'Processing your request...';
        },
        successMessage() {
            const messages = {
                'subscription': 'Subscription created successfully!',
                'upgrade': 'Plan upgraded successfully!',
                'payment': 'Payment processed successfully!',
            };
            return messages[this.context] || 'Success!';
        },
        errorMessage() {
            return 'Something went wrong';
        },
        errorDetails() {
            const details = {
                'subscription': 'We had trouble setting up your subscription. Please try again or contact support.',
                'upgrade': 'We had trouble upgrading your plan. Please try again or contact support.',
                'payment': 'We had trouble processing your payment. Please try again or contact support.',
            };
            return details[this.context] || 'Please try again or contact support if the problem persists.';
        }
    },
    mounted() {
        this.startPolling();
    },
    beforeUnmount() {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
        }
    },
    methods: {
        startPolling() {
            this.pollInterval = setInterval(() => {
                this.checkStatus();
            }, 500);
            
            // Also check immediately
            this.checkStatus();
        },
        openIntercom() {
            window.Intercom('showNewMessage');
        },
        
        async checkStatus() {
            this.tries++;
            
            try {
                const response = await axios.get('/api/stripe/check-status', {
                    params: {
                        context: this.context,
                        session_id: this.sessionId
                    }
                });
                
                if (response.data.status === 'completed') {
                    this.status = 'success';
                    clearInterval(this.pollInterval);
                    
                    // Redirect after showing success
                    setTimeout(() => {
                        if (response.data.redirect_url) {
                            window.location.href = response.data.redirect_url;
                        } else {
                            this.$inertia.get(this.getDefaultRedirect());
                        }
                    }, 2000);
                    
                } else if (response.data.status === 'failed') {
                    this.status = 'error';
                    clearInterval(this.pollInterval);
                    
                } else {
                    // Still processing - check if we've exceeded max tries
                    if (this.tries >= this.maxTries) {
                        this.status = 'error';
                        clearInterval(this.pollInterval);
                    }
                }
                
            } catch (error) {
                console.error('Error checking status:', error);
                if (this.tries >= this.maxTries) {
                    this.status = 'error';
                    clearInterval(this.pollInterval);
                }
            }
        },
        
        retry() {
            this.status = 'checking';
            this.tries = 0;
            this.startPolling();
        },
        
        getDefaultRedirect() {
            const redirects = {
                'subscription': route('select-number'),
                'upgrade': route('my.subscription'),
                'payment': route('dashboard'),
            };
            return redirects[this.context] || route('dashboard');
        }
    }
};
</script>

<style scoped>
.animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: .5;
    }
}
</style>